import type { Router } from 'vue-router'

import PAGE_NAME from '@web/consts/PAGE_NAME'
import { clearOnboardingProgress, readOnboardingProgress, writeOnboardingProgress } from './progress'
import { ONBOARDING_SLUG, type OnboardingSlug } from './slugs'
import { URL_SLUG } from './urlSlugs'
import type { OnboardingContext, OnboardingDestination, OnboardingProgress } from './types'

// Duplicated from registry to avoid circular import resolver <-> registry.
// Source of truth: registry.ts scenarios[slug].steps[].urlSlug.
// Must be updated if scenario step urlSlugs change.
const KNOWN_SCENARIO_SLUGS: Record<string, ReadonlySet<string>> = {
  [ONBOARDING_SLUG.INF_SP]: new Set<string>([
    URL_SLUG.PROFILE_UPDATE,
    URL_SLUG.VERIFY_PHONE,
    URL_SLUG.WELCOME,
    URL_SLUG.LIVE_EVENTS,
    URL_SLUG.CLUB_SUPPORT,
    URL_SLUG.READY_INF_SP
  ]),
  [ONBOARDING_SLUG.START_SELFIE]: new Set<string>([
    URL_SLUG.PROFILE_UPDATE,
    URL_SLUG.VERIFY_PHONE,
    URL_SLUG.WELCOME,
    URL_SLUG.SELFIE,
    URL_SLUG.READY_START_SELFIE
  ]),
  [ONBOARDING_SLUG.START]: new Set<string>([
    URL_SLUG.PROFILE_UPDATE,
    URL_SLUG.VERIFY_PHONE,
    URL_SLUG.WELCOME,
    URL_SLUG.READY_START
  ]),
  [ONBOARDING_SLUG.ACCOUNT_SETUP]: new Set<string>([
    URL_SLUG.ACCOUNT_PHONE,
    URL_SLUG.ACCOUNT_PROFILE
  ])
}

// account-setup steps use legacy routeName-based routes for backward compat.
// Must be updated if account-setup scenario routeNames change.
const KNOWN_STEP_ROUTE_NAMES: Record<string, Partial<Record<string, string>>> = {
  [ONBOARDING_SLUG.ACCOUNT_SETUP]: {
    [URL_SLUG.ACCOUNT_PHONE]: PAGE_NAME.PHONE_VERIFICATION,
    [URL_SLUG.ACCOUNT_PROFILE]: PAGE_NAME.UPDATE_PROFILE
  }
}

export const ONBOARDING_STEP_ROUTE_NAMES: ReadonlySet<string> = new Set(
  Object.values(KNOWN_STEP_ROUTE_NAMES)
    .flatMap(steps => Object.values(steps))
    .filter((name): name is string => typeof name === 'string')
)

const ACCOUNT_SETUP_STEP_ROUTE_NAMES: ReadonlySet<string> = new Set(
  Object.values(KNOWN_STEP_ROUTE_NAMES[ONBOARDING_SLUG.ACCOUNT_SETUP] ?? {})
    .filter((name): name is string => typeof name === 'string')
)

/**
 * Returns true when the user is currently navigating one of the legacy
 * account-setup routes AND has saved progress for that scenario.
 * Used by middleware to detect "inside account-setup" state.
 */
export function isInsideAccountSetupRoute (routeName: string): boolean {
  if (!ACCOUNT_SETUP_STEP_ROUTE_NAMES.has(routeName)) return false
  const progress = readOnboardingProgress()
  return progress?.slug === ONBOARDING_SLUG.ACCOUNT_SETUP
}

function getStepRouteName (slug: string, step: string): string | undefined {
  return KNOWN_STEP_ROUTE_NAMES[slug]?.[step]
}

function makeOnboardingDest (slug: string, step: string): OnboardingDestination {
  return { kind: 'onboarding', slug, step, routeName: getStepRouteName(slug, step) }
}

export function pickSegmentSlug (ctx: OnboardingContext): OnboardingSlug {
  if (ctx.isShowNewLk && ctx.hasSuperSubscription) return ONBOARDING_SLUG.INF_SP
  if (ctx.hasSelfieCourse) return ONBOARDING_SLUG.START_SELFIE
  return ONBOARDING_SLUG.START
}

/**
 * Saved progress is "actionable" only when the scenario is still relevant
 * for the current user. Stale progress (e.g. user finished phone validation
 * elsewhere but progress still points to account-setup) gets cleared.
 */
function isProgressActionable (
  progress: OnboardingProgress,
  ctx: OnboardingContext
): boolean {
  if (progress.slug === ONBOARDING_SLUG.ACCOUNT_SETUP) {
    return ctx.user.requirePhoneValidate || ctx.user.requiredUpdateProfile
  }
  // Segmented scenarios (inf_sp / start_selfie / start) — progress is actionable
  // only if the slug matches the user's current segment.
  if (
    progress.slug === ONBOARDING_SLUG.INF_SP ||
    progress.slug === ONBOARDING_SLUG.START_SELFIE ||
    progress.slug === ONBOARDING_SLUG.START
  ) {
    return pickSegmentSlug(ctx) === progress.slug
  }
  return false
}

export function resolveOnboardingDestination (ctx: OnboardingContext): OnboardingDestination {
  const progress = readOnboardingProgress()
  if (progress && !isProgressActionable(progress, ctx)) {
    clearOnboardingProgress()
  }

  // Сохранённый прогресс имеет приоритет над force-логикой: если юзер
  // ушёл с онбоарда посередине, мы вернём его на тот же шаг (даже если
  // флаги бэка ещё активны), а не сбросим в самое начало.
  const validProgress = readOnboardingProgress()
  if (validProgress) {
    const allowedSlugs = KNOWN_SCENARIO_SLUGS[validProgress.slug]
    if (allowedSlugs && allowedSlugs.has(validProgress.step)) {
      return makeOnboardingDest(validProgress.slug, validProgress.step)
    }
  }

  if (ctx.user.requirePhoneValidate || ctx.user.requiredUpdateProfile) {
    return makeOnboardingDest(pickSegmentSlug(ctx), URL_SLUG.PROFILE_UPDATE)
  }

  if (ctx.isTake) return { kind: 'home' }

  return { kind: 'home' }
}

export function resolveAfterScenarioComplete (
  _slug: string,
  _ctx: OnboardingContext
): OnboardingDestination {
  return { kind: 'home' }
}

export function shouldCompleteOnboarding (ctx: OnboardingContext): boolean {
  if (ctx.user.requirePhoneValidate) return true
  if (ctx.user.requiredUpdateProfile) return true
  return false
}

export function applyDestination (router: Router, dest: OnboardingDestination): void {
  switch (dest.kind) {
    case 'onboarding':
      if (dest.routeName) {
        writeOnboardingProgress({ slug: dest.slug, step: dest.step })
        router.push({ name: dest.routeName })
      } else {
        router.push({
          name: PAGE_NAME.ONBOARDING,
          params: { name: dest.slug, step: dest.step }
        })
      }
      return
    case 'home':
      router.push({ name: PAGE_NAME.HOME })
      return
    case 'returnUrl':
      router.push(dest.url)
      return
    case 'external':
      window.location.href = dest.url
  }
}
